import { useState } from 'react';
import { useNextStep } from '../../app/navigation';
import { Screen } from '../../chrome';
import { Button, Hint, PickList, Question, Sub, type PickOption } from '../../components';
import { useT } from '../../i18n';
import { useTrack } from '../../lib/analytics';
import { useSetSelfReport, type WhatHappened } from '../../lib/api';
import { StageError, TripSummary } from '../S1/stage';
import { primaryIdent, useStage } from '../S1/trip';

/**
 * S4 · What happened? Only when the data can't tell us: no status, the passenger disputed
 * S5, or "Something else" on the homepage (the resolver decides). `diverted` is the
 * prototype's "It landed somewhere else"; the missed connection only on trips with one.
 */
export default function ScreenS4() {
  const t = useT();
  const track = useTrack();
  const { enquiryId, enquiry, trip, error } = useStage('S4');
  const setSelfReport = useSetSelfReport(enquiryId);
  const { goNext } = useNextStep();

  const options: PickOption<WhatHappened>[] = [
    { value: 'delayed', label: t('S4.delayed') },
    { value: 'cancelled', label: t('S4.cancelled') },
    { value: 'denied_boarding', label: t('S4.denied') },
    { value: 'diverted', label: t('S4.diverted') },
    ...(trip.connections.length ? [{ value: 'missed_connection' as const, label: t('S4.missed') }] : []),
  ];
  // What they told us before, else the homepage's "What went wrong?" (prototype).
  const entry = enquiry?.entry;
  const preset: WhatHappened | null =
    enquiry?.selfReport?.whatHappened ?? (entry === 'cancelled' || entry === 'delayed' ? entry : null);
  const [picked, setPicked] = useState<WhatHappened | null>(null);
  const value = picked ?? preset;
  const ident = primaryIdent(enquiry);
  const question = ident ? t('S4.titleFlight', { ident }) : t('S4.title');

  async function onContinue() {
    if (!value) return;
    track('s4_answered', { whatHappened: value });
    const e = await setSelfReport.mutateAsync({ whatHappened: value });
    await goNext(e);
  }

  return (
    <Screen
      id="S4"
      title={t('S4.title')}
      dock={
        <Button
          onClick={() => void onContinue().catch(() => {})}
          disabled={!value || setSelfReport.isPending}
        >
          {t('common.continue')}
        </Button>
      }
    >
      {error ? (
        <StageError />
      ) : (
        <>
          <TripSummary enquiry={enquiry} trip={trip} editTo="S3" />
          <Question>{question}</Question>
          <Sub>{t('S4.sub')}</Sub>
          <PickList options={options} value={value} onChange={setPicked} label={question} />
          {setSelfReport.isError && <Hint warn>{t('S4.saveFailed')}</Hint>}
        </>
      )}
    </Screen>
  );
}
